import { z } from 'zod';

enum PurchaseRequirementType {
  Item = 'item',
  AnyItemFromCategory = 'category',
  ApplyDiscountToAllItems = 'allItems',
  ApplyDiscountToSpecificItems = 'specificItems',
  Cart = 'cart',
}

enum PurchaseRequirementRelation {
  AND = 'AND',
  OR = 'OR',
}

const item = z.object({ quantity: z.coerce.number().gte(1).optional(), itemName: z.string().min(1), variantName: z.string().min(1) });

const selectedVariant = z.object({
  name: z.string(),
  price_min: z.number().nullable(),
  price_max: z.number().nullable(),
  item_variant_ids: z.array(z.number()),
  checked: z.boolean(),
});

const selectedItem = z.object({
  name: z.string(),
  business_id: z.number(),
  outlet_ids: z.array(z.number()),
  item_ids: z.array(z.number()),
  checked: z.boolean(),
  item_variants: z.array(selectedVariant),
});

const promoPurchaseRequirementSchema = z
  .object({
    requirementType: z.nativeEnum(PurchaseRequirementType),
    items: z.array(item).optional(),
    relation: z.nativeEnum(PurchaseRequirementRelation).optional(),
    categoryQuantity: z.coerce.number().optional(),
    categoryName: z.string().optional(),
    selectedItems: z.array(selectedItem).optional(),
    amount: z.coerce.number().optional(),
  })
  .superRefine(({ requirementType, items, categoryQuantity, categoryName, selectedItems, amount }, ctx) => {
    if (requirementType === PurchaseRequirementType.Cart && (!amount || amount < 1)) {
      return ctx.addIssue({
        path: ['amount'],
        message: '*Minimum purchase amount must greater than 0',
        code: 'custom',
      });
    }

    if (requirementType === PurchaseRequirementType.Item && items && items.length === 0) {
      return ctx.addIssue({
        path: ['items'],
        message: '*Please add at least 1 item',
        code: 'custom',
      });
    }

    if (requirementType === PurchaseRequirementType.AnyItemFromCategory && (!categoryQuantity || categoryQuantity < 1)) {
      return ctx.addIssue({
        path: ['categoryQuantity'],
        code: 'too_small',
        minimum: 1,
        type: 'number',
        inclusive: true,
        message: '*Insert category quantity value at least 1',
      });
    }

    if (requirementType === PurchaseRequirementType.AnyItemFromCategory && !categoryName) {
      return ctx.addIssue({
        path: ['categoryName'],
        message: '*Please select one of the category',
        code: 'custom',
      });
    }

    if (requirementType === PurchaseRequirementType.ApplyDiscountToSpecificItems && selectedItems && selectedItems.length === 0) {
      return ctx.addIssue({
        path: ['selectedItems'],
        message: '*Please assign at least 1 item',
        code: 'custom',
      });
    }

    return undefined;
  });

type PurchaseRequirementSchemaType = z.infer<typeof promoPurchaseRequirementSchema>;

export type { PurchaseRequirementSchemaType };

export { PurchaseRequirementType, PurchaseRequirementRelation, promoPurchaseRequirementSchema };
